// INC2-INTAKE (build order increment 2 A; §3 "One intake", "Handler-DE routing"; integrations
// design §2.4, §2.5): the notify receiver's two doors, the handler rules, and the operator's view
// of recent inbound events and of the integration work queue's dead letters.
//
//   POST {co}/integrations/notify/{id}       a vendor notice for one subscription, verified by
//                                            its preset (Google channel token, HubSpot v3,
//                                            GoHighLevel Ed25519, CompanyOS `t=,v1=`)
//   POST /v1/integrations/notify/{provider}  a vendor app's notice (one URL per app): the
//                                            app's signature first, then the account → company
//   GET  {co}/intake/events                  recent inbound events (managers; never a payload)
//   GET  {co}/intake/jobs                    integration jobs (managers): dead letters first
//   POST {co}/intake/jobs/{id}/retry         a dead letter, queued again now
//   POST {co}/intake/jobs/{id}/discard       a dead letter, given up
//   GET  {co}/intake/rules                   the handler rules (managers)
//   PUT  {co}/intake/rules                   one source's rule, made or replaced
//   DELETE {co}/intake/rules/{id}            a rule removed (its source falls back)
import { z } from "zod";
import { InboundEventStatus, InboundSourceKind, IntegrationJobState } from "../enums";
import {
  InboundEvent,
  InboundHandlerRule,
  InboundHandlerRulePut,
  IntegrationJob,
  NotifyReceipt,
} from "../intake-rows";
import { Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "intake" } as const;

export const IntakeEventsQuery = PageQuery.extend({
  status: InboundEventStatus.optional(),
  source_kind: InboundSourceKind.optional(),
});
export const IntakeJobsQuery = PageQuery.extend({ state: IntegrationJobState.optional() });
export const NotifyAppParams = z.object({ provider: z.enum(["ghl", "hubspot"]) });

const NOTIFY_DESCRIPTION =
  "Called by the vendor, never by a person. Until the notice verifies every refusal is 401 " +
  "alike (an unknown subscription, a bad or stale signature, a body over 256 KiB or not whole " +
  "within 15 s). Once verified: a paused subscription 503 with `Retry-After`; not JSON 422; " +
  "over the subscription's or the company's inbound rate 429. The same notice again (the " +
  "vendor's delivery id, or the same signed content) answers 202 `duplicate` and writes " +
  "nothing. Accepted: 202, one `inbound_event` (source, preset, payload kept for 30 days, " +
  "screen verdict) and `inbound_event.received`; a notice is a reason to pull, never data, so " +
  "it also queues the subscription's pull (one waiting pull per subscription).";

export const intakeRoutes = {
  "integrations.notify": defineRoute({
    ...core,
    id: "integrations.notify",
    method: "post",
    path: `${co}/integrations/notify/{id}`,
    summary: "A vendor's change notice for one subscription (verified by its preset)",
    description: NOTIFY_DESCRIPTION,
    access: "hook_signature",
    params: CompanyItemParams,
    body: z.unknown(),
    response: { status: 202, schema: NotifyReceipt },
    errors: ["hook_paused"],
    emits: ["inbound_event.received"],
  }),
  "integrations.notifyApp": defineRoute({
    ...core,
    id: "integrations.notifyApp",
    method: "post",
    path: "/v1/integrations/notify/{provider}",
    summary: "A vendor app's notice (GoHighLevel, HubSpot): one URL per app, routed by account",
    description:
      `${NOTIFY_DESCRIPTION} The app's signature is checked before anything is read; then the ` +
      "account the body names (GoHighLevel `locationId`, HubSpot `portalId`) is looked up: an " +
      "account no company subscribed answers 202 `ignored` and writes nothing.",
    access: "vendor_signature",
    params: NotifyAppParams,
    body: z.unknown(),
    response: { status: 202, schema: NotifyReceipt },
    errors: ["hook_paused"],
    emits: ["inbound_event.received"],
  }),
  "intake.events": defineRoute({
    ...core,
    id: "intake.events",
    method: "get",
    path: `${co}/intake/events`,
    summary:
      "Recent inbound events: source, preset, screen verdict, handler and task, newest first",
    access: "manager",
    params: CompanyParams,
    query: IntakeEventsQuery,
    response: { status: 200, schema: page(InboundEvent) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "intake.jobs": defineRoute({
    ...core,
    id: "intake.jobs",
    method: "get",
    path: `${co}/intake/jobs`,
    summary: "Integration jobs (pulls, renewals, acts): queued, done, dead letters, newest first",
    access: "manager",
    params: CompanyParams,
    query: IntakeJobsQuery,
    response: { status: 200, schema: page(IntegrationJob) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "intake.retryJob": defineRoute({
    ...core,
    id: "intake.retryJob",
    method: "post",
    path: `${co}/intake/jobs/{id}/retry`,
    summary: "Queue a dead letter again now",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: IntegrationJob },
    errors: ["integration_job_state_conflict"],
    emits: ["integration_job.resolved"],
  }),
  "intake.discardJob": defineRoute({
    ...core,
    id: "intake.discardJob",
    method: "post",
    path: `${co}/intake/jobs/{id}/discard`,
    summary: "Give up a dead letter",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: IntegrationJob },
    errors: ["integration_job_state_conflict"],
    emits: ["integration_job.resolved"],
  }),
  "intake.rules": defineRoute({
    ...core,
    id: "intake.rules",
    method: "get",
    path: `${co}/intake/rules`,
    summary: "Handler rules: per source a default handler DE, the owners' assistants, the CoS",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(InboundHandlerRule) }) },
    errors: [],
    emits: [],
  }),
  "intake.putRule": defineRoute({
    ...core,
    id: "intake.putRule",
    method: "put",
    path: `${co}/intake/rules`,
    summary: "Make or replace one source's handler rule",
    description:
      "One rule per (kind, source key). A handler is an active digital employee; an `owner` " +
      "rule names a person's assistant (the source key is that person's membership id) and the " +
      "`company` rule (source key `default`) the Chief of Staff. A `hook` rule's handler is the " +
      "hook's target too: setting one moves the hook.",
    access: "manager",
    params: CompanyParams,
    body: InboundHandlerRulePut,
    idempotency: "optional",
    response: { status: 200, schema: InboundHandlerRule },
    errors: [],
    emits: ["inbound_rule.changed"],
  }),
  "intake.deleteRule": defineRoute({
    ...core,
    id: "intake.deleteRule",
    method: "delete",
    path: `${co}/intake/rules/{id}`,
    summary: "Remove a handler rule (its source falls back to the owner's assistant, the CoS)",
    access: "manager",
    params: z.object({ co: Uuid, id: Uuid }),
    response: { status: 200, schema: InboundHandlerRule },
    errors: [],
    emits: ["inbound_rule.changed"],
  }),
} as const;
